import React, { useState, useEffect, useRef } from 'react';
import { Mail, MessageSquare, User, Phone, Send, CheckCircle, MapPin } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../utils/firebase';

gsap.registerPlugin(ScrollTrigger);

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const infoRef = useRef<HTMLDivElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const successRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const title = titleRef.current;
    const subtitle = subtitleRef.current;
    const form = formRef.current;
    const info = infoRef.current;
    const submitBtn = submitButtonRef.current;

    if (!section || !title || !subtitle || !form || !info) return;

    // Initial states
    gsap.set([title, subtitle], {
      opacity: 0,
      y: 50
    });

    gsap.set(form, {
      opacity: 0,
      x: 50,
      scale: 0.95
    });

    gsap.set(info, {
      opacity: 0,
      x: -50,
      scale: 0.95
    });

    // Animate background parallax
    gsap.to(section, {
      backgroundPositionY: '30px',
      duration: 10,
      ease: 'none',
      repeat: -1,
      yoyo: true
    });

    // Scroll-triggered animations
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 80%",
        end: "bottom 20%",
        toggleActions: "play none none reverse"
      }
    });

    tl.to(title, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: "back.out(1.7)"
    })
      .to(subtitle, {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power2.out"
      }, "-=0.4")
      .to([form, info], {
        opacity: 1,
        x: 0,
        scale: 1,
        duration: 0.8,
        ease: "back.out(1.7)"
      }, "-=0.3");

    // Staggered entrance for form fields
    const formFields = form.querySelectorAll('input, textarea');
    gsap.set(formFields, { opacity: 0, y: 30 });
    gsap.to(formFields, {
      opacity: 1,
      y: 0,
      duration: 0.6,
      ease: "power2.out",
      stagger: 0.15,
      delay: 1.2
    });

    // Animate icons in info section
    const icons = info.querySelectorAll('svg');
    gsap.set(icons, { scale: 0, rotation: -90 });
    gsap.to(icons, {
      scale: 1,
      rotation: 0,
      duration: 0.7,
      ease: "back.out(1.7)",
      stagger: 0.1,
      delay: 1.2
    });

    // Floating animation for submit button
    if (submitBtn) {
      gsap.to(submitBtn, {
        y: -4,
        duration: 2.5,
        ease: "power1.inOut",
        yoyo: true,
        repeat: -1,
        delay: 2
      });
    }

    // Animate form fields on focus
    formFields.forEach((field) => {
      const fieldElement = field as HTMLElement;
      gsap.set(fieldElement, { scale: 1 });
      fieldElement.addEventListener('focus', () => {
        gsap.to(fieldElement, {
          scale: 1.02,
          duration: 0.3,
          ease: "power2.out"
        });
      });
      fieldElement.addEventListener('blur', () => {
        gsap.to(fieldElement, {
          scale: 1,
          duration: 0.3,
          ease: "power2.out"
        });
      });
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const button = submitButtonRef.current;
    const form = formRef.current;

    if (!button || !form) return;

    try {
      // Animate button loading state
      gsap.to(button, {
        scale: 0.95,
        duration: 0.2,
        ease: "power2.out"
      });

      // Add document to Firestore
      await addDoc(collection(db, "contactUs"), {
        name: formData.name,
        phone: formData.mobile,
        email: formData.email,
        message: formData.message,
        time: serverTimestamp()
      });

      // Animate form out
      gsap.to(form, {
        opacity: 0,
        y: -20,
        duration: 0.5,
        ease: "power2.in"
      });

      setIsSubmitted(true);
      setIsSubmitting(false);

      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        gsap.to(form, {
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: "power2.out"
        });
        setFormData({ name: '', mobile: '', email: '', message: '' });
      }, 3000);
    } catch (error) {
      console.error("Error submitting form:", error);
      setIsSubmitting(false);
      // You might want to show an error message to the user here
    }
  };

  const handleInputChange = (field: string, value: string) => {
    // Check if the field is required and contains only whitespace
    const requiredFields = ['name', 'mobile', 'email', 'message'];
    const input = document.querySelector(`input[name="${field}"], textarea[name="${field}"]`) as HTMLInputElement | HTMLTextAreaElement;

    if (requiredFields.includes(field) && input) {
      if (value.trim() === '') {
        input.setCustomValidity('This field cannot be empty or contain only spaces');
      } else {
        input.setCustomValidity('');
      }
    }

    setFormData(prev => ({ ...prev, [field]: value }));

    // Add subtle animation to input field
    if (input) {
      gsap.to(input, {
        scale: 1.01,
        duration: 0.1,
        ease: "power2.out",
        yoyo: true,
        repeat: 1
      });
    }
  };

  return (
    <section ref={sectionRef} id="contact-form-section" className="py-20 bg-gradient-to-b from-white to-[#F5F5F5] overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 ref={titleRef} className="text-3xl font-bold text-[#1A1A1A] mb-4">Let's Start Your Electric Journey</h2>
            <p ref={subtitleRef} className="text-gray-600 max-w-2xl mx-auto">
              Whether you're interested in our passenger or loader vehicles, or have questions about our technology,
              our team is here to help you explore the future of electric mobility with Dawki Mobility.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div ref={infoRef} className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-[#1A1A1A] mb-4">Why Choose Dawki Mobility?</h3>
                <ul className="space-y-4">
                  {[
                    "Expert consultation on electric three-wheeler solutions",
                    "Comprehensive test drive experiences",
                    "Customized vehicle configurations for your needs",
                    "Nationwide service and support network",
                    "Flexible financing and leasing options",
                    "Complete after-sales support and maintenance"
                  ].map((item, index) => (
                    <li
                      key={index}
                      className="flex items-start cursor-pointer transform-gpu"
                      onMouseEnter={(e) => {
                        gsap.to(e.currentTarget, {
                          x: 5,
                          duration: 0.3,
                          ease: "power2.out"
                        });
                      }}
                      onMouseLeave={(e) => {
                        gsap.to(e.currentTarget, {
                          x: 0,
                          duration: 0.3,
                          ease: "power2.out"
                        });
                      }}
                    >
                      <span className="text-[#CC0000] mr-2">•</span>
                      <span className="text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-[#1A1A1A] mb-4">Authorized Dealer</h3>
                <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-lg font-semibold text-[#CC0000] mb-2">EV STAR MOTORS</h4>
                      <div className="flex items-center text-gray-600 mb-2">
                        <Phone size={16} className="mr-2 text-[#CC0000]" />
                        <span className="font-medium">8830005759</span>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h5 className="font-medium text-gray-800 mb-2">Showroom Address:</h5>
                          <p className="text-gray-600 text-sm leading-relaxed">
                            Gat no, 1617, Near Annapurna Bhaji Market,<br />
                            Dehu - Alandi Rd, Patilnagar, Chikhali,<br />
                            Pimpri-Chinchwad, Pune, Maharashtra 411062
                          </p>
                        </div>
                        <button
                          onClick={() => window.open('https://www.google.com/maps/dir//no+8A,+Success+Industrial+Park,+Plot+No+63+Gat+No+445,+opposite+Mahindra+gate,+Nighoje,+Maharashtra+410501/@18.7189121,73.7322235,12z/data=!4m8!4m7!1m0!1m5!1m1!1s0x3bc2b72ee611fe57:0xfc40545f192a0f8f!2m2!1d73.8146252!2d18.7189301?entry=ttu&g_ep=EgoyMDI1MDkxNC4wIKXMDSoASAFQAw%3D%3D', '_blank')}
                          className="ml-4 bg-[#CC0000] hover:bg-[#AA0000] text-white p-3 rounded-full transition-all duration-300 transform-gpu hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#CC0000] focus:ring-offset-2 group"
                          title="Open in Google Maps"
                          onMouseEnter={(e) => {
                            gsap.to(e.currentTarget, {
                              scale: 1.15,
                              rotation: 5,
                              duration: 0.3,
                              ease: "power2.out"
                            });
                          }}
                          onMouseLeave={(e) => {
                            gsap.to(e.currentTarget, {
                              scale: 1,
                              rotation: 0,
                              duration: 0.3,
                              ease: "power2.out"
                            });
                          }}
                        >
                          <MapPin size={20} className="group-hover:animate-pulse" />
                        </button>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <p className="text-sm text-gray-500 italic">
                        Visit our showroom to experience Dawki Mobility's electric three-wheelers firsthand.
                        Our experts will help you find the perfect vehicle for your needs.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-lg shadow-lg p-8 transform-gpu">
              {!isSubmitted ? (
                <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      <User size={16} className="mr-2" />
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#CC0000] focus:border-transparent transition-all duration-300"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      <Phone size={16} className="mr-2" />
                      Mobile Number
                    </label>
                    <input
                      type="tel"
                      name="mobile"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#CC0000] focus:border-transparent transition-all duration-300"
                      value={formData.mobile}
                      onChange={(e) => handleInputChange('mobile', e.target.value)}
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>

                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      <Mail size={16} className="mr-2" />
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#CC0000] focus:border-transparent transition-all duration-300"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      <MessageSquare size={16} className="mr-2" />
                      Message
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={8}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#CC0000] focus:border-transparent transition-all duration-300"
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      placeholder="Tell us about your transportation needs or any questions you have about our electric vehicles..."
                    ></textarea>
                  </div>

                  <button
                    ref={submitButtonRef}
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#CC0000] hover:bg-[#AA0000] text-white font-medium py-3 px-6 rounded-md transition-all duration-300 flex items-center justify-center space-x-2 cursor-pointer transform-gpu"
                    onMouseEnter={() => {
                      if (!isSubmitting) {
                        gsap.to(submitButtonRef.current, {
                          scale: 1.02,
                          duration: 0.3,
                          ease: "power2.out"
                        });
                      }
                    }}
                    onMouseLeave={() => {
                      if (!isSubmitting) {
                        gsap.to(submitButtonRef.current, {
                          scale: 1,
                          duration: 0.3,
                          ease: "power2.out"
                        });
                      }
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send size={20} />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>
                </form>
              ) : (
                <div ref={successRef} className="text-center py-12">
                  <div className="mb-6">
                    <CheckCircle size={64} className="text-green-500 mx-auto" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#1A1A1A] mb-4">Message Sent Successfully!</h3>
                  <p className="text-gray-600">
                    Thank you for contacting us. We'll get back to you within 24 hours.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;